import * as Api from '@ViewModels';
import { PaperClipIcon } from '@WebComponents/svgs/icons/PaperClipIcon';
import { css, StyleSheet } from 'aphrodite';
import { nanoid } from 'nanoid';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import {
	Attachment,
	type AttachmentData,
	AttachmentInfo,
	AttachmentPreview,
	AttachmentRemove,
	Attachments,
} from '../../../../components-v2/ai/Attachments';
import {
	AI_CHAT_FILE_ACCEPT,
	AI_CHAT_MAX_FILE_SIZE,
	AI_CHAT_MAX_FILES,
	useAIChatAttachments,
} from '../../../../components-v2/ai/PromptInput/PromptInputAttachments';
import { Button } from '../../../../components-v2/Button';
import { THEME } from '../../../../components-v2/Theme/theme';
import { bs } from '../../../../styles/styles';

export type UseGenerationAttachmentsResult = {
	files: AttachmentData[];
	pendingIds: ReadonlySet<string>;
	isUploading: boolean;
	/** Server-side ids for the files that finished uploading, in the order the user attached them. */
	fileAttachmentIds: string[];
	add: (incoming: FileList | File[]) => void;
	remove: (localId: string) => void;
	clear: () => void;
};

/**
 * Upload lifecycle for files attached to the content calendar generation form.
 *
 * The composer gets this from PromptInput; the form has no PromptInput, so it owns the local
 * file list itself and drives the same {@link useAIChatAttachments} controller underneath.
 */
export function useGenerationAttachments({
	impersonationContext,
}: {
	impersonationContext?: Api.IImpersonationContext;
} = {}): UseGenerationAttachmentsResult {
	const { uploadedById, pendingIds, isUploading, upload, forget, reset } = useAIChatAttachments({
		impersonationContext,
	});
	const [files, setFiles] = useState<AttachmentData[]>([]);
	const filesRef = useRef(files);
	filesRef.current = files;

	const remove = useCallback(
		(localId: string) => {
			setFiles(prev => {
				const found = prev.find(file => file.id === localId);
				if (found?.url) {
					URL.revokeObjectURL(found.url);
				}
				return prev.filter(file => file.id !== localId);
			});
			forget(localId);
		},
		[forget]
	);

	const add = useCallback(
		(incoming: FileList | File[]) => {
			const sized = Array.from(incoming).filter(file => {
				if (file.size > AI_CHAT_MAX_FILE_SIZE) {
					toast.error(`${file.name} is larger than ${AI_CHAT_MAX_FILE_SIZE / 1_000_000}MB.`);
					return false;
				}
				return true;
			});
			const capacity = Math.max(0, AI_CHAT_MAX_FILES - filesRef.current.length);
			if (sized.length > capacity) {
				toast.error(`You can attach up to ${AI_CHAT_MAX_FILES} files.`);
			}
			const accepted = sized.slice(0, capacity);
			if (accepted.length === 0) {
				return;
			}
			const added: AttachmentData[] = accepted.map(file => ({
				id: nanoid(),
				type: 'file',
				url: URL.createObjectURL(file),
				mediaType: file.type,
				filename: file.name,
				file,
			}));
			setFiles(prev => [...prev, ...added]);
			for (const entry of added) {
				upload(entry.id, entry.file!).then(result => {
					if (!result) {
						toast.error(`Failed to upload ${entry.filename}`);
						remove(entry.id);
					}
				});
			}
		},
		[upload, remove]
	);

	const clear = useCallback(() => {
		setFiles(prev => {
			for (const file of prev) {
				if (file.url) {
					URL.revokeObjectURL(file.url);
				}
			}
			return [];
		});
		reset();
	}, [reset]);

	useEffect(
		() => () => {
			for (const file of filesRef.current) {
				if (file.url) {
					URL.revokeObjectURL(file.url);
				}
			}
		},
		[]
	);

	return {
		files,
		pendingIds,
		isUploading,
		fileAttachmentIds: files.map(file => uploadedById[file.id]?.id).filter(Boolean),
		add,
		remove,
		clear,
	};
}

export type GenerationFormAttachmentsProps = {
	attachments: UseGenerationAttachmentsResult;
	disabled?: boolean;
};

export const GenerationFormAttachments = ({ attachments, disabled }: GenerationFormAttachmentsProps) => {
	const { files, pendingIds, add, remove } = attachments;
	const inputRef = useRef<HTMLInputElement>(null);
	const isFull = files.length >= AI_CHAT_MAX_FILES;

	return (
		<div className={css(s.container)}>
			<input
				accept={AI_CHAT_FILE_ACCEPT}
				aria-label='Attach files'
				className={css(bs.hidden)}
				multiple
				onChange={event => {
					if (event.currentTarget.files) {
						add(event.currentTarget.files);
					}
					event.currentTarget.value = '';
				}}
				ref={inputRef}
				type='file'
			/>
			<div className={css(s.controlRow)}>
				<Button
					disabled={disabled || isFull}
					onClick={() => inputRef.current?.click()}
					size='sm'
					styles={[s.attachButton]}
					variant='secondary'
				>
					<PaperClipIcon />
					Attach files
				</Button>
				<span className={css(s.hint)}>
					{isFull ? `${AI_CHAT_MAX_FILES} file limit reached` : 'PDF, text, CSV, or images — up to 5 files, 20MB each'}
				</span>
			</div>
			{files.length > 0 ? (
				<Attachments variant='inline' styles={[s.attachments]}>
					{files.map(file =>
						pendingIds.has(file.id) ? (
							<Attachment key={file.id} data={file}>
								<div className={css(s.spinnerSlot)}>
									<LoadingSpinner type='tiny' />
								</div>
								<AttachmentInfo />
							</Attachment>
						) : (
							<Attachment data={file} key={file.id} onRemove={() => remove(file.id)}>
								<AttachmentPreview />
								<AttachmentInfo />
								<AttachmentRemove />
							</Attachment>
						)
					)}
				</Attachments>
			) : null}
		</div>
	);
};

const s = StyleSheet.create({
	container: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
	},
	controlRow: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		flexWrap: 'wrap',
	},
	attachButton: {
		gap: THEME.spacing.xs,
	},
	hint: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
	},
	attachments: {
		marginLeft: 0,
	},
	spinnerSlot: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 20,
		height: 20,
		flexShrink: 0,
	},
});
